/**
 * @fileoverview Audit log query builder (MOR-1012).
 *
 * The "Fields" panel above the audit table, in the same shape as the
 * timesheets report builder: a period select, a field list that toggles
 * the table's columns, and a Filters zone of "Field is any of …" chips.
 * Pure client state — the panel applies the query to the in-memory index.
 */

import { useMemo, useState } from "react";
import {
  Check,
  ChevronDown,
  Filter,
  GripVertical,
  Plus,
  X,
} from "lucide-react";
import { cn } from "@/utils/misc";
import { Button } from "@/ui/primitives/button";
import { Checkbox } from "@/ui/primitives/checkbox";
import { Input } from "@/ui/primitives/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/ui/composed/sections/collapsible";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/ui/composed/menus/command";

import {
  ALL_AUDIT_COLUMNS,
  AUDIT_COLUMN_LABELS,
  AUDIT_PERIOD_LABELS,
  type AuditColumn,
  type AuditCondition,
  type AuditDimension,
  type AuditFilterField,
  type AuditPeriod,
  type AuditQuery,
} from "./-ui.job-audit-query";

// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------

const CHIP =
  "inline-flex items-center gap-1 rounded-md border bg-background px-1.5 py-1 text-xs";

export function AuditQueryBuilder({
  query,
  onChange,
  dimensions,
}: {
  query: AuditQuery;
  onChange: (patch: Partial<AuditQuery>) => void;
  dimensions: ReadonlyArray<AuditDimension>;
}) {
  const [open, setOpen] = useState(true);
  const [fieldSearch, setFieldSearch] = useState("");
  /** The condition being composed; `index` is null for a new one. */
  const [editing, setEditing] = useState<{
    field: AuditFilterField;
    index: number | null;
    values: string[];
  } | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const dimensionFor = (field: AuditFilterField) =>
    dimensions.find((d) => d.field === field);

  const visibleColumns = useMemo(() => {
    const needle = fieldSearch.trim().toLowerCase();
    return ALL_AUDIT_COLUMNS.filter((c) =>
      AUDIT_COLUMN_LABELS[c].toLowerCase().includes(needle),
    );
  }, [fieldSearch]);

  const toggleColumn = (column: AuditColumn) => {
    const next = query.columns.includes(column)
      ? query.columns.filter((c) => c !== column)
      : ALL_AUDIT_COLUMNS.filter(
          (c) => c === column || query.columns.includes(c),
        );
    // A table needs at least one column.
    if (next.length === 0) return;
    onChange({ columns: next });
  };

  const commitCondition = (negate: boolean) => {
    if (!editing || editing.values.length === 0) return;
    const condition: AuditCondition = {
      field: editing.field,
      negate,
      values: editing.values,
    };
    const next = [...query.conditions];
    if (editing.index === null) next.push(condition);
    else next[editing.index] = condition;
    onChange({ conditions: next });
    setEditing(null);
  };

  const removeCondition = (index: number) =>
    onChange({ conditions: query.conditions.filter((_, i) => i !== index) });

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="rounded-lg border bg-card p-4"
    >
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-1.5 text-sm font-semibold"
        >
          <ChevronDown
            className={cn("size-4 transition-transform", !open && "-rotate-90")}
          />
          Fields
        </button>
      </CollapsibleTrigger>

      <CollapsibleContent className="mt-4 flex flex-col gap-4">
        {/* Period */}
        <div className="flex flex-wrap items-end gap-6 border-b pb-4">
          <label className="flex flex-col gap-1.5 text-sm font-medium">
            Period
            <Select
              value={query.period}
              onValueChange={(v) => onChange({ period: v as AuditPeriod })}
            >
              <SelectTrigger className="h-10 w-[220px] text-sm font-normal">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(AUDIT_PERIOD_LABELS) as AuditPeriod[]).map(
                  (p) => (
                    <SelectItem key={p} value={p}>
                      {AUDIT_PERIOD_LABELS[p]}
                    </SelectItem>
                  ),
                )}
              </SelectContent>
            </Select>
          </label>
        </div>

        <div className="grid gap-3 lg:grid-cols-[minmax(220px,300px)_1fr]">
          {/* Field list → table columns */}
          <div className="flex flex-col gap-2 rounded-lg border p-3">
            <Input
              value={fieldSearch}
              onChange={(e) => setFieldSearch(e.target.value)}
              placeholder="Search fields..."
              className="h-9 text-sm"
            />
            <span className="mt-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              Columns
            </span>
            <ul className="flex flex-col gap-1">
              {visibleColumns.map((column) => {
                const checked = query.columns.includes(column);
                return (
                  <li key={column}>
                    <label className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm hover:bg-muted/50">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleColumn(column)}
                      />
                      <GripVertical className="size-3 text-muted-foreground/40" />
                      {AUDIT_COLUMN_LABELS[column]}
                    </label>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Filters zone */}
          <div className="flex min-h-[120px] flex-col gap-1.5 rounded-lg border border-dashed border-border/70 bg-muted/30 p-2">
            <div className="flex items-center gap-1.5">
              <Filter className="size-3.5 text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground">
                Filters
              </span>
              <span className="ml-auto">
                <Popover open={addOpen} onOpenChange={setAddOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 gap-1 text-xs"
                    >
                      <Plus className="size-3" />
                      Add filter
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[220px] p-1" align="end">
                    {dimensions.map((d) => (
                      <button
                        key={d.field}
                        type="button"
                        className="flex w-full items-center rounded px-2 py-1.5 text-left text-sm hover:bg-muted"
                        onClick={() => {
                          setAddOpen(false);
                          setEditing({
                            field: d.field,
                            index: null,
                            values: [],
                          });
                        }}
                      >
                        {d.label}
                      </button>
                    ))}
                  </PopoverContent>
                </Popover>
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-1">
              {query.conditions.map((condition, index) => (
                <ConditionChip
                  key={`${condition.field}:${index}`}
                  dimension={dimensionFor(condition.field)}
                  condition={condition}
                  open={editing?.index === index}
                  onOpenChange={(o) =>
                    setEditing(
                      o
                        ? {
                            field: condition.field,
                            index,
                            values: condition.values,
                          }
                        : null,
                    )
                  }
                  values={editing?.index === index ? editing.values : []}
                  onValuesChange={(values) =>
                    setEditing((prev) => (prev ? { ...prev, values } : prev))
                  }
                  onApply={commitCondition}
                  onRemove={() => removeCondition(index)}
                />
              ))}
              {editing?.index === null && (
                <ConditionChip
                  dimension={dimensionFor(editing.field)}
                  open
                  onOpenChange={(o) => !o && setEditing(null)}
                  values={editing.values}
                  onValuesChange={(values) =>
                    setEditing((prev) => (prev ? { ...prev, values } : prev))
                  }
                  onApply={commitCondition}
                  onRemove={() => setEditing(null)}
                />
              )}
            </div>
            {query.conditions.length === 0 && !editing && (
              <span className="text-xs italic text-muted-foreground/70">
                Showing every change — add a filter to narrow the log
              </span>
            )}
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

// ---------------------------------------------------------------------------
// Condition chip + value picker
// ---------------------------------------------------------------------------

function ConditionChip({
  dimension,
  condition,
  open,
  onOpenChange,
  values,
  onValuesChange,
  onApply,
  onRemove,
}: {
  dimension: AuditDimension | undefined;
  /** Absent while the condition is still being composed. */
  condition?: AuditCondition;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  values: string[];
  onValuesChange: (values: string[]) => void;
  onApply: (negate: boolean) => void;
  onRemove: () => void;
}) {
  const label = dimension?.label ?? condition?.field ?? "Field";
  const summary = condition
    ? condition.values
        .map(
          (value) =>
            dimension?.options.find((o) => o.value === value)?.label ?? value,
        )
        .slice(0, 2)
        .join(", ") +
      (condition.values.length > 2 ? ` +${condition.values.length - 2}` : "")
    : "pick values";

  return (
    <span className={cn(CHIP, !condition && "border-dashed")}>
      <Popover open={open} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>
          <button type="button" className="flex items-center gap-1 text-left">
            <span className="font-medium">{label}</span>
            <span className="text-muted-foreground">
              {condition?.negate ? "is none of" : "is any of"}
            </span>
            <span className="max-w-[240px] truncate">{summary}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-0" align="start">
          <Command>
            <CommandInput
              placeholder={`Search ${(dimension?.label ?? "values").toLowerCase()}…`}
            />
            <CommandList>
              <CommandEmpty>No matches.</CommandEmpty>
              <CommandGroup>
                {(dimension?.options ?? []).map((option) => {
                  const selected = values.includes(option.value);
                  return (
                    <CommandItem
                      key={option.value}
                      value={option.label}
                      onSelect={() =>
                        onValuesChange(
                          selected
                            ? values.filter((v) => v !== option.value)
                            : [...values, option.value],
                        )
                      }
                    >
                      <div
                        className={cn(
                          "mr-2 flex size-4 shrink-0 items-center justify-center rounded-sm border border-primary",
                          selected
                            ? "bg-primary text-primary-foreground"
                            : "opacity-50 [&_svg]:invisible",
                        )}
                      >
                        <Check className="size-3" />
                      </div>
                      <span className="min-w-0 flex-1 truncate">
                        {option.label}
                      </span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
          <div className="flex items-center justify-end gap-1.5 border-t border-border/60 p-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              disabled={values.length === 0}
              onClick={() => onApply(true)}
              title="Exclude changes matching these values"
            >
              Apply NOT
            </Button>
            <Button
              type="button"
              size="sm"
              className="h-7 text-xs"
              disabled={values.length === 0}
              onClick={() => onApply(false)}
            >
              Apply
            </Button>
          </div>
        </PopoverContent>
      </Popover>
      <button
        type="button"
        className="rounded p-0.5 text-muted-foreground hover:text-destructive"
        onClick={onRemove}
        aria-label={`Remove the ${label} filter`}
      >
        <X className="size-3" />
      </button>
    </span>
  );
}
